import {
  AnimatedDeleteIcon,
  Avatar,
  Divider,
  MenuItem,
  Menu,
  type MenuProps,
} from '@affine/component';
import { UserPlanButton } from '@affine/core/components/affine/auth/user-plan-button';
import {
  type AuthAccountInfo,
  AuthService,
  ServerService,
} from '@affine/core/modules/cloud';
import {
  GlobalDialogService,
  WorkspaceDialogService,
} from '@affine/core/modules/dialogs';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { CollaborationIcon, ImportIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { type SyntheticEvent, useCallback } from 'react';

import { Account } from './account';
import { AccountMenu } from './account-menu';
import { AIUsage } from './ai-usage';
import { CloudUsage } from './cloud-usage';
import * as styles from './index.css';
import { TeamList } from './team-list';
import { UnknownUserIcon } from './unknow-user';

/**
 * Sidebar footer left slot: the signed-in user's avatar, name and plan badge
 * (opens the account menu on click), or a sign-in button when logged out.
 */
export default function UserInfo({
  onOpenImport,
}: {
  onOpenImport?: () => void;
}) {
  const session = useService(AuthService).session;
  const account = useLiveData(session.account$);
  return account ? (
    <AuthorizedUserInfo account={account} onOpenImport={onOpenImport} />
  ) : (
    <UnauthorizedUserInfo />
  );
}

const menuContentOptions: MenuProps['contentOptions'] = {
  className: styles.operationMenu,
};

const AuthorizedUserInfo = ({
  account,
  onOpenImport,
}: {
  account: AuthAccountInfo;
  onOpenImport?: () => void;
}) => {
  const workspaceDialogService = useService(WorkspaceDialogService);

  // The badge sits inside the menu trigger; Radix opens the menu on
  // pointerdown, so swallow pointer events in the capture phase and let only
  // the badge's own click through — it jumps to the plans tab.
  const swallowPointer = useCallback((e: SyntheticEvent) => {
    e.stopPropagation();
  }, []);

  const onOpenPlans = useCallback(
    (e: SyntheticEvent<Element, Event>) => {
      e.stopPropagation();
      workspaceDialogService.open('setting', {
        activeTab: 'plans',
      });
    },
    [workspaceDialogService]
  );

  return (
    <Menu
      items={<OperationMenu onOpenImport={onOpenImport} />}
      contentOptions={menuContentOptions}
    >
      <button
        type="button"
        data-testid="sidebar-user-avatar"
        className={styles.userRow}
      >
        <Avatar
          size={28}
          rounded={50}
          name={account.label}
          url={account.avatar}
        />
        <span className={styles.userName} title={account.label}>
          {account.label}
        </span>
        <span
          className={styles.planBadgeSlot}
          onPointerDownCapture={swallowPointer}
          onPointerUpCapture={swallowPointer}
        >
          <UserPlanButton onClick={onOpenPlans} />
        </span>
      </button>
    </Menu>
  );
};

const UnauthorizedUserInfo = () => {
  const globalDialogService = useService(GlobalDialogService);
  const t = useI18n();

  const openSignInModal = useCallback(() => {
    globalDialogService.open('sign-in', {});
  }, [globalDialogService]);

  return (
    <button
      type="button"
      onClick={openSignInModal}
      data-testid="sidebar-user-avatar"
      className={styles.userRow}
    >
      <UnknownUserIcon width={28} height={28} />
      <span className={styles.userName}>{t['com.affine.auth.sign.in']()}</span>
    </button>
  );
};

const OperationMenu = ({ onOpenImport }: { onOpenImport?: () => void }) => {
  const serverService = useService(ServerService);
  const serverFeatures = useLiveData(serverService.server.features$);
  const workbench = useService(WorkbenchService).workbench;
  const workspace = useService(WorkspaceService).workspace;
  const workspaceDialogService = useService(WorkspaceDialogService);
  const t = useI18n();

  const isLocal = workspace.flavour === 'local';

  const onOpenInviteMembers = useCallback(() => {
    workspaceDialogService.open('setting', {
      activeTab: `workspace:members`,
    });
  }, [workspaceDialogService]);

  return (
    <>
      <Account />
      <Divider />
      <MenuItem
        prefixIcon={<AnimatedDeleteIcon closed />}
        data-testid="workspace-modal-trash-option"
        onClick={() => workbench.openTrash()}
      >
        {t['com.affine.workspaceSubPath.trash']()}
      </MenuItem>
      <MenuItem
        prefixIcon={<ImportIcon />}
        data-testid="workspace-modal-import-option"
        onClick={onOpenImport}
      >
        {t['Import']()}
      </MenuItem>
      {!isLocal ? (
        <MenuItem
          prefixIcon={<CollaborationIcon />}
          data-testid="workspace-modal-invite-members-option"
          onClick={onOpenInviteMembers}
        >
          {t['Invite Members']()}
        </MenuItem>
      ) : null}
      <Divider />
      <CloudUsage />
      {serverFeatures?.copilot ? <AIUsage /> : null}
      <Divider />
      <TeamList />
      <AccountMenu />
    </>
  );
};
